import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Plus, Trash2, Check, X, ChevronLeft, ChevronRight, Pencil, AlertTriangle } from 'lucide-react';
import { getAccessToken } from '../../services/auth';

const BOOL_VALUES = new Set(['true', 'false']);
const isBoolField = (value) => value !== null && value !== undefined && BOOL_VALUES.has(String(value).toLowerCase());

const FIELD_OPTIONS = {
  'Ship Status': ['In Stock', 'Fresh', 'Frozen', 'Live'],
};

const READONLY_FIELDS = new Set(['CPX_Product_Id']);

function SmartInput({ value, onChange, onKeyDown, autoFocus, existingValue, fieldName }) {
  const inputStyle = { width: '100%', padding: '4px 8px', border: '1px solid var(--orange)', borderRadius: 4, fontSize: 13 };

  if (READONLY_FIELDS.has(fieldName)) {
    return <span style={{ fontSize: 13, color: 'var(--gray-400)', fontStyle: 'italic' }}>{value} (read-only)</span>;
  }

  if (FIELD_OPTIONS[fieldName]) {
    return (
      <select value={value} onChange={e => onChange(e.target.value)} autoFocus={autoFocus} style={inputStyle}>
        {FIELD_OPTIONS[fieldName].map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );
  }

  if (isBoolField(existingValue) || isBoolField(value)) {
    return (
      <select value={value} onChange={e => onChange(e.target.value)} autoFocus={autoFocus} style={inputStyle}>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }

  return <input value={value} onChange={e => onChange(e.target.value)} onKeyDown={onKeyDown} autoFocus={autoFocus} style={inputStyle} />;
}

const FUNC_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8888/.netlify/functions/bc-proxy'
  : '/.netlify/functions/bc-proxy';

async function bcApi(method, path, body) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(FUNC_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({ method, path, body }),
  });
  const data = await res.json();
  if (!res.ok) {
    const detail = data?.errors ? JSON.stringify(data.errors) : data?.title || data?.error || 'BC API error';
    throw new Error(detail);
  }
  return data;
}

const PAGE_SIZE = 50;

// ─── Tab styles ────────────────────────────────────────────────────────────
function Tab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 18px',
        fontSize: 13,
        fontWeight: active ? 700 : 500,
        color: active ? 'var(--orange)' : 'var(--gray-500)',
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid var(--orange)' : '2px solid transparent',
        cursor: 'pointer',
        marginBottom: -1,
      }}
    >
      {label}
    </button>
  );
}

// ─── By Product Tab ─────────────────────────────────────────────────────────
function ByProductTab() {
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [productPage, setProductPage] = useState(1);
  const [productSearch, setProductSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [fields, setFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [fieldsError, setFieldsError] = useState(null);

  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState({});
  const [deleting, setDeleting] = useState({});
  const [newField, setNewField] = useState({ name: '', value: '' });
  const [addingField, setAddingField] = useState(false);
  const [addError, setAddError] = useState(null);

  const loadProducts = useCallback(async (page, search) => {
    setProductsLoading(true);
    setProductsError(null);
    try {
      const qs = `?limit=${PAGE_SIZE}&page=${page}&include_fields=id,name,sku${search ? '&keyword=' + encodeURIComponent(search) : ''}`;
      const data = await bcApi('GET', `/v3/catalog/products${qs}`);
      setProducts(data.data || []);
      setTotalProducts(data.meta?.pagination?.total || 0);
    } catch (e) {
      setProductsError(e.message);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  useEffect(() => { loadProducts(productPage, productSearch); }, [loadProducts, productPage, productSearch]);

  const loadFields = useCallback(async (productId) => {
    setFieldsLoading(true);
    setFieldsError(null);
    setEditing({});
    setNewField({ name: '', value: '' });
    setAddError(null);
    try {
      const data = await bcApi('GET', `/v3/catalog/products/${productId}/custom-fields?limit=100`);
      setFields(data.data || []);
    } catch (e) {
      setFieldsError(e.message);
    } finally {
      setFieldsLoading(false);
    }
  }, []);

  const selectProduct = (product) => { setSelectedProduct(product); loadFields(product.id); };

  const handleSearch = (e) => { e.preventDefault(); setProductPage(1); setProductSearch(searchInput.trim()); };

  const startEdit = (field) => setEditing(prev => ({ ...prev, [field.id]: { name: field.name, value: field.value } }));
  const cancelEdit = (id) => setEditing(prev => { const n = { ...prev }; delete n[id]; return n; });

  const saveEdit = async (fieldId) => {
    const draft = editing[fieldId];
    if (!draft) return;
    setSaving(prev => ({ ...prev, [fieldId]: true }));
    try {
      const data = await bcApi('PUT', `/v3/catalog/products/${selectedProduct.id}/custom-fields/${fieldId}`, { name: draft.name, value: draft.value });
      setFields(prev => prev.map(f => f.id === fieldId ? { ...f, name: data.data?.name ?? draft.name, value: data.data?.value ?? draft.value } : f));
      cancelEdit(fieldId);
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setSaving(prev => { const n = { ...prev }; delete n[fieldId]; return n; }); }
  };

  const deleteField = async (fieldId) => {
    if (!window.confirm('Delete this custom field?')) return;
    setDeleting(prev => ({ ...prev, [fieldId]: true }));
    try {
      await bcApi('DELETE', `/v3/catalog/products/${selectedProduct.id}/custom-fields/${fieldId}`);
      setFields(prev => prev.filter(f => f.id !== fieldId));
    } catch (e) { alert('Delete failed: ' + e.message); }
    finally { setDeleting(prev => { const n = { ...prev }; delete n[fieldId]; return n; }); }
  };

  const addField = async () => {
    if (!newField.name.trim()) { setAddError('Name is required'); return; }
    setAddingField(true);
    setAddError(null);
    try {
      const data = await bcApi('POST', `/v3/catalog/products/${selectedProduct.id}/custom-fields`, { name: newField.name.trim(), value: newField.value.trim() });
      setFields(prev => [...prev, data.data]);
      setNewField({ name: '', value: '' });
    } catch (e) { setAddError(e.message); }
    finally { setAddingField(false); }
  };

  const totalPages = Math.ceil(totalProducts / PAGE_SIZE);

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* Left: product list */}
      <div style={{ width: 300, flexShrink: 0, background: 'var(--white)', border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--gray-200)' }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
            <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search name or SKU..."
              style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--gray-300)', borderRadius: 5, fontSize: 13 }} />
            <button type="submit" style={{ padding: '6px 10px', background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <Search size={14} />
            </button>
          </form>
          <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 6 }}>
            {productsLoading ? 'Loading…' : `${totalProducts.toLocaleString()} products`}
          </div>
        </div>
        <div style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
          {productsError && <div style={{ padding: '12px 14px', color: 'var(--red)', fontSize: 13 }}>{productsError}</div>}
          {products.map(product => (
            <div key={product.id} onClick={() => selectProduct(product)} style={{
              padding: '10px 14px', borderBottom: '1px solid var(--gray-100)', cursor: 'pointer',
              background: selectedProduct?.id === product.id ? '#fff7ed' : undefined,
              borderLeft: selectedProduct?.id === product.id ? '3px solid var(--orange)' : '3px solid transparent',
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-900)', marginBottom: 2 }}>{product.name}</div>
              <div style={{ fontSize: 11, color: 'var(--gray-400)', fontFamily: 'monospace' }}>{product.sku}</div>
            </div>
          ))}
          {!productsLoading && products.length === 0 && (
            <div style={{ padding: '20px 14px', textAlign: 'center', color: 'var(--gray-400)', fontSize: 13 }}>No products found</div>
          )}
        </div>
        {totalPages > 1 && (
          <div style={{ padding: '10px 14px', borderTop: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button onClick={() => setProductPage(p => Math.max(1, p - 1))} disabled={productPage <= 1}
              style={{ background: 'none', border: '1px solid var(--gray-300)', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'var(--gray-600)' }}>
              <ChevronLeft size={13} />
            </button>
            <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>Page {productPage} of {totalPages}</span>
            <button onClick={() => setProductPage(p => Math.min(totalPages, p + 1))} disabled={productPage >= totalPages}
              style={{ background: 'none', border: '1px solid var(--gray-300)', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'var(--gray-600)' }}>
              <ChevronRight size={13} />
            </button>
          </div>
        )}
      </div>

      {/* Right: custom fields editor */}
      <div style={{ flex: 1, background: 'var(--white)', border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' }}>
        {!selectedProduct ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>
            <div style={{ fontSize: 15, marginBottom: 8 }}>Select a product</div>
            <div style={{ fontSize: 13 }}>Choose a product from the list to view and edit its custom fields.</div>
          </div>
        ) : (
          <>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--gray-900)' }}>{selectedProduct.name}</div>
              <div style={{ fontSize: 12, color: 'var(--gray-400)', fontFamily: 'monospace', marginTop: 2 }}>
                SKU: {selectedProduct.sku} &nbsp;·&nbsp; ID: {selectedProduct.id}
              </div>
            </div>
            {fieldsLoading ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)', fontSize: 13 }}>Loading…</div>
            ) : fieldsError ? (
              <div style={{ padding: 24, color: 'var(--red)', fontSize: 13 }}>{fieldsError}</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)' }}>
                      <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--gray-500)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--gray-200)', width: 200 }}>Field Name</th>
                      <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--gray-500)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--gray-200)' }}>Value</th>
                      <th style={{ padding: '9px 14px', textAlign: 'center', fontWeight: 600, color: 'var(--gray-500)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--gray-200)', width: 110 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map(field => {
                      const isEditing = !!editing[field.id];
                      const isSaving = !!saving[field.id];
                      const isDeleting = !!deleting[field.id];
                      const draft = editing[field.id] || {};
                      return (
                        <tr key={field.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                          <td style={{ padding: '8px 14px', verticalAlign: 'middle' }}>
                            {isEditing ? (
                              <input value={draft.name} onChange={e => setEditing(prev => ({ ...prev, [field.id]: { ...prev[field.id], name: e.target.value } }))}
                                style={{ width: '100%', padding: '4px 8px', border: '1px solid var(--orange)', borderRadius: 4, fontSize: 13 }} />
                            ) : (
                              <span style={{ cursor: 'text', color: 'var(--gray-800)', fontWeight: 500 }} onClick={() => startEdit(field)} title="Click to edit">{field.name}</span>
                            )}
                          </td>
                          <td style={{ padding: '8px 14px', verticalAlign: 'middle' }}>
                            {isEditing ? (
                              <SmartInput
                                value={draft.value}
                                existingValue={field.value}
                                fieldName={field.name}
                                onChange={v => setEditing(prev => ({ ...prev, [field.id]: { ...prev[field.id], value: v } }))}
                                onKeyDown={e => { if (e.key === 'Enter') saveEdit(field.id); if (e.key === 'Escape') cancelEdit(field.id); }}
                                autoFocus />
                            ) : (
                              <span style={{ cursor: 'text', color: 'var(--gray-700)', wordBreak: 'break-all' }} onClick={() => startEdit(field)} title="Click to edit">
                                {field.value || <span style={{ color: 'var(--gray-300)', fontStyle: 'italic' }}>empty</span>}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '8px 14px', textAlign: 'center', verticalAlign: 'middle' }}>
                            {isEditing ? (
                              <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                                <button onClick={() => saveEdit(field.id)} disabled={isSaving} title="Save"
                                  style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                  <Check size={13} />
                                </button>
                                <button onClick={() => cancelEdit(field.id)} title="Cancel"
                                  style={{ background: 'var(--gray-200)', color: 'var(--gray-600)', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                  <X size={13} />
                                </button>
                              </div>
                            ) : READONLY_FIELDS.has(field.name) ? (
                              <span style={{ fontSize: 11, color: 'var(--gray-300)', fontStyle: 'italic' }}>locked</span>
                            ) : (
                              <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                <button onClick={() => startEdit(field)} title="Edit field"
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 4, display: 'inline-flex', alignItems: 'center' }}>
                                  <Pencil size={14} />
                                </button>
                                <button onClick={() => deleteField(field.id)} disabled={isDeleting} title="Delete field"
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 4, display: 'inline-flex', alignItems: 'center' }}>
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Add new field row */}
                    <tr style={{ background: 'var(--gray-50)', borderTop: fields.length > 0 ? '2px solid var(--gray-200)' : undefined }}>
                      <td style={{ padding: '10px 14px' }}>
                        <input value={newField.name} onChange={e => setNewField(prev => ({ ...prev, name: e.target.value }))} placeholder="Field name"
                          style={{ width: '100%', padding: '5px 8px', border: `1px solid ${addError ? 'var(--red)' : 'var(--gray-300)'}`, borderRadius: 4, fontSize: 13 }} />
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <input value={newField.value} onChange={e => setNewField(prev => ({ ...prev, value: e.target.value }))} placeholder="Value"
                          onKeyDown={e => { if (e.key === 'Enter') addField(); }}
                          style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13 }} />
                        {addError && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 3 }}>{addError}</div>}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <button onClick={addField} disabled={addingField}
                          style={{ background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600 }}>
                          <Plus size={13} /> Add
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                {fields.length === 0 && !fieldsLoading && (
                  <div style={{ padding: '12px 18px', color: 'var(--gray-400)', fontSize: 13, textAlign: 'center' }}>No custom fields yet. Add one below.</div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Field Audit Tab ─────────────────────────────────────────────────────────
function FieldAuditTab() {
  const [allProducts, setAllProducts] = useState([]);       // [{id, name, sku, customFields: []}]
  const [loading, setLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState('');
  const [error, setError] = useState(null);

  const [selectedField, setSelectedField] = useState('');
  const [filterMode, setFilterMode] = useState('all');      // 'all' | 'missing' | 'true' | 'false'
  const [sortAsc, setSortAsc] = useState(true);

  // inline editing in audit view
  const [auditEditing, setAuditEditing] = useState({});     // { [productId]: draftValue }
  const [auditSaving, setAuditSaving] = useState({});

  // Fetch all products with custom fields embedded
  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAllProducts([]);
    try {
      let page = 1;
      let totalPages = 1;
      const collected = [];
      do {
        setLoadProgress(`Loading products… page ${page}${totalPages > 1 ? ' of ' + totalPages : ''}`);
        const data = await bcApi('GET', `/v3/catalog/products?limit=${PAGE_SIZE}&page=${page}&include=custom_fields`);
        const prods = data.data || [];
        prods.forEach(p => collected.push({ id: p.id, name: p.name, sku: p.sku, customFields: p.custom_fields || [] }));
        totalPages = data.meta?.pagination?.total_pages || 1;
        page++;
      } while (page <= totalPages);
      setAllProducts(collected);
      setLoadProgress('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // All unique field names across all products
  const fieldNames = Array.from(
    new Set(allProducts.flatMap(p => p.customFields.map(f => f.name)))
  ).sort();

  // Set default selected field when data loads
  useEffect(() => {
    if (fieldNames.length > 0 && !selectedField) setSelectedField(fieldNames[0]);
  }, [fieldNames, selectedField]);

  // Build rows for the selected field
  const rows = allProducts.map(p => {
    const field = p.customFields.find(f => f.name === selectedField);
    return { productId: p.id, productName: p.name, sku: p.sku, fieldId: field?.id ?? null, value: field?.value ?? null };
  });

  const filtered = rows.filter(r => {
    if (filterMode === 'missing') return r.value === null;
    if (filterMode === 'set') return r.value !== null;
    if (filterMode === 'true') return r.value?.toLowerCase() === 'true';
    if (filterMode === 'false') return r.value?.toLowerCase() === 'false';
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = a.value ?? '\uFFFF';
    const bv = b.value ?? '\uFFFF';
    return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const missingCount = rows.filter(r => r.value === null).length;

  // Save from audit view
  const saveAuditEdit = async (row) => {
    const draft = auditEditing[row.productId];
    if (draft === undefined) return;
    setAuditSaving(prev => ({ ...prev, [row.productId]: true }));
    try {
      if (row.fieldId) {
        // Update existing
        await bcApi('PUT', `/v3/catalog/products/${row.productId}/custom-fields/${row.fieldId}`, { name: selectedField, value: draft });
      } else {
        // Create new
        const data = await bcApi('POST', `/v3/catalog/products/${row.productId}/custom-fields`, { name: selectedField, value: draft });
        // Store the new field id back
        setAllProducts(prev => prev.map(p => p.id === row.productId
          ? { ...p, customFields: [...p.customFields, data.data] }
          : p
        ));
      }
      // Update local allProducts value
      setAllProducts(prev => prev.map(p => {
        if (p.id !== row.productId) return p;
        const existing = p.customFields.find(f => f.name === selectedField);
        if (existing) {
          return { ...p, customFields: p.customFields.map(f => f.name === selectedField ? { ...f, value: draft } : f) };
        }
        return { ...p, customFields: [...p.customFields, { id: null, name: selectedField, value: draft }] };
      }));
      setAuditEditing(prev => { const n = { ...prev }; delete n[row.productId]; return n; });
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setAuditSaving(prev => { const n = { ...prev }; delete n[row.productId]; return n; });
    }
  };

  const cancelAuditEdit = (productId) => setAuditEditing(prev => { const n = { ...prev }; delete n[productId]; return n; });

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>
        <div style={{ fontSize: 14, marginBottom: 8 }}>{loadProgress || 'Loading…'}</div>
        <div style={{ fontSize: 12 }}>Fetching all products with custom fields</div>
      </div>
    );
  }

  if (error) {
    return <div style={{ padding: 24, color: 'var(--red)', fontSize: 13 }}>{error}</div>;
  }

  return (
    <div style={{ background: 'var(--white)', border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' }}>
      {/* Controls bar */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Field</label>
          <select value={selectedField} onChange={e => { setSelectedField(e.target.value); setFilterMode('all'); }}
            style={{ padding: '5px 10px', border: '1px solid var(--gray-300)', borderRadius: 5, fontSize: 13, minWidth: 180 }}>
            {fieldNames.map(name => <option key={name} value={name}>{name}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Show</label>
          {['all', 'missing', 'set', 'true', 'false'].map(mode => (
            <button key={mode} onClick={() => setFilterMode(mode)}
              style={{
                padding: '4px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer', fontWeight: filterMode === mode ? 700 : 400,
                background: filterMode === mode ? 'var(--orange)' : 'var(--gray-100)',
                color: filterMode === mode ? '#fff' : 'var(--gray-600)',
                border: 'none',
              }}>
              {mode === 'all' ? 'All' : mode === 'missing' ? `Missing (${missingCount})` : mode === 'set' ? 'Has Value' : mode}
            </button>
          ))}
        </div>

        <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--gray-400)' }}>
          {sorted.length} product{sorted.length !== 1 ? 's' : ''}
          {missingCount > 0 && filterMode !== 'missing' && (
            <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <AlertTriangle size={12} /> {missingCount} missing
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <tr style={{ background: 'var(--gray-50)' }}>
              <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--gray-500)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--gray-200)' }}>Product</th>
              <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--gray-500)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--gray-200)', width: 140 }}>SKU</th>
              <th
                onClick={() => setSortAsc(a => !a)}
                style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--gray-500)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--gray-200)', width: 160, cursor: 'pointer', userSelect: 'none' }}>
                Value {sortAsc ? '▲' : '▼'}
              </th>
              <th style={{ padding: '9px 14px', textAlign: 'center', fontWeight: 600, color: 'var(--gray-500)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--gray-200)', width: 90 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(row => {
              const isMissing = row.value === null;
              const isEditing = auditEditing[row.productId] !== undefined;
              const isSaving = !!auditSaving[row.productId];
              const draftValue = auditEditing[row.productId] ?? '';
              return (
                <tr key={row.productId} style={{ borderBottom: '1px solid var(--gray-100)', background: isMissing ? '#fef2f2' : undefined }}>
                  <td style={{ padding: '8px 14px', color: 'var(--gray-900)', fontWeight: 500 }}>{row.productName}</td>
                  <td style={{ padding: '8px 14px', color: 'var(--gray-500)', fontFamily: 'monospace', fontSize: 12 }}>{row.sku}</td>
                  <td style={{ padding: '8px 14px' }}>
                    {isEditing ? (
                      <SmartInput
                        value={draftValue}
                        existingValue={row.value}
                        fieldName={selectedField}
                        onChange={v => setAuditEditing(prev => ({ ...prev, [row.productId]: v }))}
                        onKeyDown={e => { if (e.key === 'Enter') saveAuditEdit(row); if (e.key === 'Escape') cancelAuditEdit(row.productId); }}
                        autoFocus />
                    ) : isMissing ? (
                      <span style={{ color: '#dc2626', fontStyle: 'italic', fontSize: 12 }}>missing</span>
                    ) : (
                      <span style={{ color: 'var(--gray-700)' }}>{row.value}</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 14px', textAlign: 'center' }}>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                        <button onClick={() => saveAuditEdit(row)} disabled={isSaving}
                          style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                          <Check size={13} />
                        </button>
                        <button onClick={() => cancelAuditEdit(row.productId)}
                          style={{ background: 'var(--gray-200)', color: 'var(--gray-600)', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                          <X size={13} />
                        </button>
                      </div>
                    ) : READONLY_FIELDS.has(selectedField) ? (
                      <span style={{ fontSize: 11, color: 'var(--gray-300)', fontStyle: 'italic' }}>locked</span>
                    ) : (
                      <button onClick={() => setAuditEditing(prev => ({ ...prev, [row.productId]: row.value ?? '' }))} title="Edit"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 4, display: 'inline-flex', alignItems: 'center' }}>
                        <Pencil size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)', fontSize: 13 }}>No products match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function CustomFields() {
  const [activeTab, setActiveTab] = useState('by-product');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 4 }}>BigCommerce</div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--gray-900)', margin: 0 }}>Custom Fields</h1>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid var(--gray-200)', marginBottom: 16, display: 'flex' }}>
        <Tab label="By Product" active={activeTab === 'by-product'} onClick={() => setActiveTab('by-product')} />
        <Tab label="Field Audit" active={activeTab === 'field-audit'} onClick={() => setActiveTab('field-audit')} />
      </div>

      {activeTab === 'by-product' ? <ByProductTab /> : <FieldAuditTab />}
    </div>
  );
}
